import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link } from 'react-router'
import { useT } from '../../i18n/useI18n'
import { ACCOUNT_PATHS } from '../../account/accountPaths'
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, resetPassword } from '../../account/accountApi'
import { useAccountRequest } from '../../account/useAccountRequest'
import { useLinkToken } from '../../account/useLinkToken'
import AccountLayout from './AccountLayout'
import { AccountError, AccountNotice, SubmitButton, TextField } from './AccountParts'

function RequestNewLink({ lead }: { lead: string }): ReactElement {
  const t = useT()
  return <p className="ac-small">{lead} <Link to={ACCOUNT_PATHS.forgotPassword}>{t('account.reset.requestNew')}</Link>.</p>
}

function ResetForm({ token }: { token: string }): ReactElement {
  const t = useT()
  const [password, setPassword] = useState<string>('')
  const [repeat, setRepeat] = useState<string>('')
  const [mismatch, setMismatch] = useState<boolean>(false)
  const { state, run } = useAccountRequest()
  if (state.status === 'done') return <AccountNotice title={t('account.reset.doneTitle')}><p>{t('account.reset.doneBody')}</p></AccountNotice>

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    const differs: boolean = password !== repeat
    setMismatch(differs)
    if (!differs) void run(() => resetPassword(token, password))
  }

  return (
    <form className="ac-form" onSubmit={submit}>
      <TextField label={t('account.reset.newPassword')} value={password} onChange={setPassword} type="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} hint={t('account.register.passwordHint', { min: PASSWORD_MIN_LENGTH })} />
      <TextField label={t('account.reset.repeat')} value={repeat} onChange={setRepeat} type="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} />
      {mismatch && <AccountError error={new Error(t('account.reset.mismatch'))} />}
      {state.status === 'failed' && <><AccountError error={state.error} /><RequestNewLink lead={t('account.reset.failedLead')} /></>}
      <SubmitButton label={t('account.reset.submit')} sending={state.status === 'sending'} />
    </form>
  )
}

export default function AccountResetPassword(): ReactElement {
  const t = useT()
  const token: string | null = useLinkToken()
  return (
    <AccountLayout title={t('account.reset.title')}>
      {token === null ? <RequestNewLink lead={t('account.reset.missingToken')} /> : <ResetForm token={token} />}
    </AccountLayout>
  )
}
